"use client";

import { useEffect } from "react";

import { ReferralSchedulingRecommendation } from "@/src/components/comms/referral-scheduling-recommendation";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import ErrorAlert from "@/src/components/error-alert";
import { AthenaPatientInfoHeader } from "@/src/components/tasks/athena-patient-info-header";
import { AthenaReferralCards, AthenaReferralCardsData } from "@/src/components/tasks/athena-referral-cards";
import { ReferringProviderSection } from "@/src/components/tasks/referring-provider-section";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { isHmoPlan } from "@/src/lib/coverage-utils";
import { useProvidersForDiagnostics } from "@/src/lib/data-fetchers/diagnosis-providers-fetchers";
import { REFERRAL_SOURCE_IDS_LABELS } from "@/src/lib/ehr/athena/athena-constants";
import { logger } from "@/src/lib/logging/logger";
import { calculateAge } from "@/src/lib/utils/date-and-phone-utils";
import { Referral } from "@/src/models/documents/referral";

export interface AthenaData extends AthenaReferralCardsData {
  departmentName: string;
  shouldAssignTo: string | null;
}

interface NiraReferralInstructionsProps {
  referral: Referral;
  departmentId?: string;
  athenaData: AthenaData | null;
  error?: string;
  taskId: string;
}

export function NiraReferralInstructions({
  referral,
  departmentId,
  athenaData,
  error,
  taskId,
}: NiraReferralInstructionsProps) {
  useEffect(() => {
    logger.info("Data from Nira", { athenaData });
  }, [athenaData]);

  const { data: providersForDiagnostics } = useProvidersForDiagnostics(referral.diagnosisList || [], departmentId);

  // If someone has an HMO plan or Tricare/VA/etc,
  const insuranceNeedsAuthorization = referral.patient?.coverages?.some(
    (coverage) => coverage.plan?.includes("Tricare") || coverage.plan?.includes("VA") || isHmoPlan(coverage.plan)
  );

  const patientMetadata = JSON.parse(athenaData?.patientDetails?.ehrMetadata || "{}");

  const patientAge = calculateAge(referral.patient?.dateOfBirth);
  const isPatientUnder18 = patientAge !== null && patientAge < 18;

  return (
    <div className="space-y-8 text-foreground">
      {(error || !athenaData) && <ErrorAlert compact={true} message={error || "No Athena data available"} />}

      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <div className="space-y-2 text-foreground">
            <AthenaPatientInfoHeader
              title="Nira Referral Instructions"
              patientDetails={athenaData?.patientDetails || null}
              document={athenaData?.document || null}
              documentPatientId={athenaData?.documentPatientId}
            />
          </div>
          <h3 className="font-semibold text-foreground my-2">Sources:</h3>
          <div className="grid grid-cols-3">
            <p className="flex items-center">
              <span className="font-medium">Referral Source: </span>
              <CopyableContent
                text={
                  REFERRAL_SOURCE_IDS_LABELS[
                    patientMetadata?.referralsourceid as keyof typeof REFERRAL_SOURCE_IDS_LABELS
                  ] || "N/A"
                }
              />
            </p>
          </div>

          {/* Referring Provider Information */}
          <ReferringProviderSection referral={referral} />

          {/*Providers List - only show if we have data */}
          {(providersForDiagnostics?.length ?? 0) > 0 && (
            <div className="mb-4 my-2">
              <h3 className="font-semibold text-foreground my-2">Usual Provider:</h3>
              <ul className="list-disc list-inside space-y-1">
                {providersForDiagnostics!.map((provider) => (
                  <li
                    key={provider.name}
                    className={`text-sm ${provider.isPreferred ? "font-semibold text-muted-foreground" : "text-muted-foreground"}`}
                  >
                    <CopyableContent text={provider.name} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ReferralSchedulingRecommendation referral={referral} title="Scheduling Recommendations:" />

          {/* extra instructions */}
          <div className="mb-4 my-2">
            <h3 className="font-semibold text-foreground my-2">Extra Instructions:</h3>
            <ul className="list-disc list-inside space-y-1">
              {isPatientUnder18 && (
                <li className="text-sm text-muted-foreground">
                  Patient is under 18 years old - they don&apos;t treat them.
                </li>
              )}
              {insuranceNeedsAuthorization && (
                <li className="text-sm text-muted-foreground">
                  Insurance needs authorization. Please add authorization by clicking, &quot;add incoming
                  authorization&quot; under the insurance policy on the Quickview screen.
                </li>
              )}
              <li className="text-sm text-muted-foreground">
                If Hospital discharge or Previous Neurologist mentioned: Update document description to one of the
                following:
                <ul className="ml-6 mt-1 list-disc space-y-1">
                  <li className="text-sm text-foreground">
                    <CopyableContent text="Referral mentions Hospital Discharge/Prev. – Requested" />
                  </li>
                  <li className="text-sm text-foreground">
                    <CopyableContent text="Referral mentions Hospital Discharge/Prev. – Still needs to be requested" />
                  </li>
                </ul>
              </li>
            </ul>
          </div>

          <div className="mt-4">
            <SyncIncompleteActions taskId={taskId} />
          </div>
        </div>
      </EhrInstructionsCard>

      <AthenaReferralCards referral={referral} athenaData={athenaData} />
    </div>
  );
}
